<script setup lang="ts">
import TextInput from "@/Components/Forms/Input/TextInput.vue";
import PhoneInput from "@/Components/Forms/Input/PhoneInput.vue";
import Checkbox from "@/Components/Forms/Input/Checkbox.vue";
import Button from "@/Components/Buttons/Button.vue";
import Counter from "@/Components/Icons/Counter.vue";
import GoogleRatingSummary from "@/Components/GoogleRatingSummary.vue";
import ProfessionalSeniorityCounter from "@/Components/Forms/Input/ProfessionalSeniorityCounter.vue";
import { router, useForm, usePage } from "@inertiajs/vue3";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import {
    User,
    Vehicle,
    VehiclePreview,
    OfferTypesEligibility,
    IncludedWarranties,
} from "@/types";
import Formula from "@/Components/Simulator/Formula.vue";
import Modal from "@/Modal/Modal.vue";
import LoginCard from "@/Components/Cards/LoginCard.vue";
import Simulator from "@/Layouts/Simulator.vue";
import { watch } from "vue";
import Calculator from "@/Components/Calculator.vue";
import PriceInput from "@/Components/Forms/Input/PriceInput.vue";
import Label from "@/Components/Forms/Label.vue";
import Error from "@/Components/Forms/Error.vue";
import { ssrIsMobile } from "@/helpers/client";

const page = usePage();
const props = defineProps<{
    vehicle: Vehicle;
    vehiclePreview: VehiclePreview;
    initialData: {
        firstname: string;
        lastname: string;
        email: string;
        phone: string;
        job: string;
        fiscal_reference_income: number;
        professional_seniority: number;
        is_main_home_owner: boolean | null;
        has_leasing: boolean | null;
        accept: boolean;
        offer: string;
        duration: number;
        first_rent: number;
        mileage: number;
        gclid: string | null;
        fbclid: string | null;
    };
    offerTypesEligibility: OfferTypesEligibility;
    minFirstRent: number | null;
    estimatedPrice: number | null;
    includedWarranties?: IncludedWarranties;
}>();

const hasIncludedWarranties = computed(
    () =>
        !!props.includedWarranties?.premium ||
        !!props.includedWarranties?.purchase_value,
);

const currentUserIsMobile = ref(ssrIsMobile());

console.log("currentUserIsMobile", currentUserIsMobile.value);
console.log("auth user", page.props.auth?.user?.email);

const { t } = useI18n();

const currentIntermediateScreen = ref(1);

const openingLoginModal = ref(false);
const openLoginModal = (val: boolean) => {
    openingLoginModal.value = val;
};

const fillFormFromAuth = () => {
    openLoginModal(false);
    router.visit(route("simulations.start", { vehicle: props.vehicle }), {
        preserveScroll: true,
    });
};

// Helper function to safely access localStorage
const getLocalStorageItem = (key: string): string | null => {
    if (typeof window !== "undefined" && window.localStorage) {
        return localStorage.getItem(key);
    }
    return null;
};

const form = useForm({
    ...props.initialData,
    professional_seniority: props.initialData.professional_seniority ?? 0,
    is_main_home_owner: props.initialData.is_main_home_owner ?? null,
    has_leasing: props.initialData.has_leasing ?? null,
    gclid: props.initialData.gclid,
    fbclid: props.initialData.fbclid,
    // « Entretien et pièces d'usure » coché dans « Votre formule » : repris à l'étape 2
    maintenance_requested: false,
});

// Update UTM values from localStorage after component is mounted (client-side only)
onMounted(() => {
    const storedGclid = getLocalStorageItem("gclid");
    const storedFbclid = getLocalStorageItem("fbclid");

    if (storedGclid && !form.gclid) {
        form.gclid = storedGclid;
    }
    if (storedFbclid && !form.fbclid) {
        form.fbclid = storedFbclid;
    }
});

const submit = () => {
    if (hasFirstRentError.value != true) {
        form.transform((data) => ({
            ...data,
            vehicle_id: props.vehicle.id,
        })).post(route("simulations.init"), {
            onError: () => {
                if (currentIntermediateScreen.value > 1) {
                    let intermediateScreenError = 5;
                    if (
                        form.errors.email ||
                        form.errors.phone ||
                        form.errors.firstname ||
                        form.errors.lastname
                    ) {
                        intermediateScreenError = 2;
                    } else if (
                        form.errors.job ||
                        form.errors.fiscal_reference_income ||
                        form.errors.professional_seniority ||
                        form.errors.is_main_home_owner
                    ) {
                        intermediateScreenError = 3;
                    }

                    currentIntermediateScreen.value = intermediateScreenError;
                }
            },
        });
    }
};

const goToScreen = (screenNumber: number) => {
    if (screenNumber <= 0) return;
    currentIntermediateScreen.value = screenNumber;
};

const isStep2Filled = computed(() => {
    return form.email && form.phone && form.firstname && form.lastname;
});

const isStep3Filled = computed(() => {
    return (
        form.job &&
        form.fiscal_reference_income &&
        form.professional_seniority != null &&
        form.is_main_home_owner !== null &&
        form.accept &&
        form.has_leasing !== null
    );
});

const currentYear = computed(() => {
    return new Date().getFullYear();
});

watch(
    () => props.initialData,
    () => {
        if (page.props.auth.user) {
            form.firstname = props.initialData.firstname;
            form.lastname = props.initialData.lastname;
            form.phone = props.initialData.phone;
            form.email = props.initialData.email;
            form.job = props.initialData.job;
            form.fiscal_reference_income =
                props.initialData.fiscal_reference_income;
            form.professional_seniority =
                props.initialData.professional_seniority;
            form.is_main_home_owner = props.initialData.is_main_home_owner;
        }
    },
);

const hasFirstRentError = computed(() => {
    return form.first_rent && form.first_rent > props.vehicle.max_first_rent;
});

const simulationForm = useForm({
    offer: props.initialData.offer,
    duration: props.initialData.duration,
    first_rent: props.initialData.first_rent,
    mileage: props.initialData.mileage,
});

const monthlyPrice = ref(props.estimatedPrice ?? 0);

console.log("monthlyPrice", monthlyPrice.value);

const simulator = ref();
const showMonthyPrice = ref(false);
const isLoading = ref(false);

const handleEndLoader = () => {
    showMonthyPrice.value = true;
};

const computeNewRevision = () => {
    isLoading.value = true;
    simulationForm.post(
        route("simulations.compute", {
            vehicle: props.vehicle.id,
        }),
        {
            preserveScroll: true,
            onSuccess: () => {
                if (page.props.flash.data && page.props.flash.data.price) {
                    monthlyPrice.value = page.props.flash.data.price;
                }

                simulationForm.get(
                    route("simulations.start", { vehicle: props.vehicle }),
                    {
                        preserveState: true,
                    },
                );

                isLoading.value = false;
            },
        },
    );
    simulationForm.defaults();
};

const showMessage = computed(() => {
    return currentIntermediateScreen.value == 1;
});
</script>

<template>
    <Simulator
        ref="simulator"
        :show-message="showMessage"
        :vehicle-preview="vehiclePreview"
        :vehicle="vehicle"
        :go-to-screen="goToScreen"
        :current-tunnel-step="1"
        :current-intermediate-step="currentIntermediateScreen"
    >
        <template #message>
            {{ t("tunnel.hello") }} <br />
            {{ t("tunnel.needSomeInformations") }}
        </template>

        <Modal :show="openingLoginModal" @close="openLoginModal(false)">
            <template #content>
                <LoginCard @logged="fillFormFromAuth" />
            </template>
        </Modal>

        <form @submit.prevent="submit">
            <div
                class="mobile-hidden"
                :class="{ visible: currentIntermediateScreen == 1 }"
            >
                <template class="mobile-only">
                    <Button
                        type="button"
                        class="form-button"
                        data-attr="simulation-start-step-1-next"
                        @click="goToScreen(2)"
                    >
                        {{ t("actions.continue") }}
                    </Button>
                </template>
            </div>

            <div
                class="mobile-hidden row-margin"
                :class="{ visible: currentIntermediateScreen == 2 }"
            >
                <div class="simulation-grid">
                    <PhoneInput
                        id="phone"
                        v-model="form.phone"
                        type="text"
                        :label="
                            t('forms.whatsYour', {
                                attr: t('attributes.phone'),
                            })
                        "
                        required
                        :message="form.errors.phone"
                    />

                    <TextInput
                        id="email"
                        v-model="form.email"
                        type="email"
                        :label="
                            t('forms.whatsYourE', {
                                attr: t('attributes.email'),
                            })
                        "
                        :message="form.errors.email"
                        required
                    />

                    <TextInput
                        id="firstname"
                        v-model="form.firstname"
                        type="text"
                        :label="
                            t('forms.whatsYour', {
                                attr: t('attributes.firstname'),
                            })
                        "
                        :message="form.errors.firstname"
                        required
                    />

                    <TextInput
                        id="lastname"
                        v-model="form.lastname"
                        type="text"
                        :label="
                            t('forms.whatsYour', {
                                attr: t('attributes.lastname'),
                            })
                        "
                        :message="form.errors.lastname"
                        required
                    />
                </div>

                <template class="mobile-only">
                    <Button
                        type="button"
                        :disabled="!isStep2Filled"
                        class="form-button"
                        data-attr="simulation-start-step-2-next"
                        @click="goToScreen(3)"
                    >
                        {{ t("actions.continue") }}
                    </Button>
                </template>
            </div>

            <div
                class="mobile-hidden row-margin"
                :class="{ visible: currentIntermediateScreen == 3 }"
            >
                <div class="simulation-grid simulation-grid--compact">
                    <div>
                        <TextInput
                            id="job"
                            v-model="form.job"
                            type="text"
                            :label="
                                t('forms.whatsYourE', {
                                    attr: t('attributes.job'),
                                })
                            "
                            :message="form.errors.job"
                            required
                        />
                    </div>

                    <div class="simulation-grid__income">
                        <Label
                            required
                            :label="
                                t('forms.whatsYour', {
                                    attr:
                                        t(
                                            'attributes.fiscal_reference_income',
                                        ) +
                                        ' ' +
                                        currentYear,
                                })
                            "
                        />
                        <PriceInput
                            v-model="form.fiscal_reference_income"
                            :min-first-rent="minFirstRent"
                            :step="500"
                            :show-buttons="false"
                            :classic="true"
                        />
                        <Error :error="form.errors.fiscal_reference_income" />
                    </div>

                    <ProfessionalSeniorityCounter
                        v-model="form.professional_seniority"
                        :label="
                            t('forms.whatsYour', {
                                attr: t('attributes.professional_seniority'),
                            })
                        "
                        :min="0"
                        :max="50"
                        :step="1"
                        required
                        :error="form.errors.professional_seniority"
                    />

                    <div class="simulation-grid__choice">
                        <span class="radio-group__label">{{
                            t("tunnel.haveLeasing")
                        }}</span>
                        <div class="radio-group">
                            <label
                                for="leasing_false"
                                class="radio-label"
                                :class="{ active: !form.has_leasing }"
                            >
                                <Checkbox
                                    id="leasing_false"
                                    v-model:checked="form.has_leasing"
                                    :value="false"
                                    type="radio"
                                />

                                <span class="radio-label__text">
                                    {{ t("actions.no") }}
                                </span>
                            </label>

                            <label
                                for="leasing_true"
                                class="radio-label"
                                :class="{ active: form.has_leasing }"
                            >
                                <Checkbox
                                    id="leasing_true"
                                    v-model:checked="form.has_leasing"
                                    :value="true"
                                    type="radio"
                                />

                                <span class="radio-label__text">
                                    {{ t("actions.yes") }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div class="simulation-grid__choice">
                        <span class="radio-group__label">{{
                            t("tunnel.isMainHomeOwner")
                        }}</span>
                        <div class="radio-group">
                            <label
                                for="home_owner_false"
                                class="radio-label"
                                :class="{ active: !form.is_main_home_owner }"
                            >
                                <Checkbox
                                    id="home_owner_false"
                                    v-model:checked="form.is_main_home_owner"
                                    :value="false"
                                    type="radio"
                                />

                                <span class="radio-label__text">
                                    {{ t("actions.no") }}
                                </span>
                            </label>

                            <label
                                for="home_owner_true"
                                class="radio-label"
                                :class="{ active: form.is_main_home_owner }"
                            >
                                <Checkbox
                                    id="home_owner_true"
                                    v-model:checked="form.is_main_home_owner"
                                    :value="true"
                                    type="radio"
                                />

                                <span class="radio-label__text">
                                    {{ t("actions.yes") }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div class="desktop-no-grid">
                        <hr />
                        <label class="accept">
                            <Checkbox
                                v-model:checked="form.accept"
                                :value="true"
                            />
                            <span class="accept--label">
                                {{ t("iAcceptThe") }}
                                <a :href="route('privacy')" target="_blank">
                                    {{ t("cgu") }}
                                </a>
                            </span>
                        </label>
                    </div>
                </div>

                <Button
                    type="submit"
                    :disabled="
                        !isStep2Filled ||
                        !isStep3Filled ||
                        hasFirstRentError != false
                    "
                    class="form-button"
                    data-attr="simulation-start-submit"
                >
                    {{ t("actions.validateInfos") }}
                </Button>

                <ul class="reassurance" data-testid="start-reassurance">
                    <!-- Note Google (même source que la fiche), sous le bouton pour rassurer -->
                    <GoogleRatingSummary :stars="5" />
                    <li>
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M5 12l5 5 9-10" />
                        </svg>
                        Sans engagement
                    </li>
                    <li v-if="hasIncludedWarranties">
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M5 12l5 5 9-10" />
                        </svg>
                        Garanties incluses
                    </li>
                    <li>
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M5 12l5 5 9-10" />
                        </svg>
                        Livraison chez vous
                    </li>
                </ul>
            </div>
        </form>

        <div
            v-if="!$page.props.auth.user && currentIntermediateScreen == 1"
            class="has-account"
        >
            <p class="has-account__message">
                {{ t("tunnel.accountAlreadyExist") }}
            </p>
            <button class="has-account__button" @click="openLoginModal(true)">
                {{ t("actions.signInV2") }}
            </button>
        </div>

        <template #sidebarContent>
            <div class="simulator__sidebar__content__calculator">
                <Calculator
                    v-model:offer="simulationForm.offer"
                    v-model:duration="simulationForm.duration"
                    v-model:first-rent="simulationForm.first_rent"
                    v-model:mileage="simulationForm.mileage"
                    :offer-types-eligibility="offerTypesEligibility"
                    :min-first-rent="minFirstRent"
                    :max-first-rent="vehicle.max_first_rent"
                    :has-first-rent-error="hasFirstRentError == true"
                    :light-mode="false"
                />

                <Formula
                    v-model:maintenance-requested="form.maintenance_requested"
                    :premium-warranty="!!includedWarranties?.premium"
                    :purchase-value-warranty="
                        !!includedWarranties?.purchase_value
                    "
                />
            </div>
        </template>

        <template #sidebarFooter>
            <template v-if="showMonthyPrice">
                <div
                    class="simulator__sidebar__footer__content"
                    :class="{
                        '--reduced-padding': simulationForm.isDirty,
                    }"
                >
                    <div class="simulator__sidebar__footer__content__result">
                        <span class="simulator__sidebar__footer__title">
                            {{ t("tunnel.yourMonthlyPrice") }}
                        </span>
                        <span class="simulator__sidebar__footer__price">
                            <span
                                v-if="showMonthyPrice"
                                class="simulator__sidebar__footer__price__value"
                                :class="{
                                    blur:
                                        (!page.props.auth.user &&
                                            currentUserIsMobile) ||
                                        (showMessage && !currentUserIsMobile) ||
                                        hasFirstRentError == true ||
                                        simulationForm.isDirty ||
                                        monthlyPrice == 0 ||
                                        isLoading,
                                }"
                            >
                                {{
                                    hasFirstRentError
                                        ? "0000"
                                        : $filters.currency(monthlyPrice, false)
                                }}
                            </span>
                            {{ t("vehicle.perMonth") }}
                        </span>
                    </div>

                    <Button
                        v-if="simulationForm.isDirty"
                        type="button"
                        class="compute-button"
                        data-attr="simulation-compute"
                        :disabled="hasFirstRentError == true"
                        @click="computeNewRevision"
                    >
                        {{ t("tunnel.computeYoutMonthlyPrice") }}
                    </Button>
                </div>
            </template>
            <template v-else>
                <Counter @animation-ended="handleEndLoader" />
                <span
                    class="compute-progress"
                    data-testid="parity-sim-footer-text"
                >
                    {{ t("tunnel.monthlyPriceComputing") }}
                </span>
            </template>
        </template>
    </Simulator>
</template>

<style lang="scss" scoped>
// Une ligne de réassurance sous le bouton, là où le client hésite à donner ses informations
.reassurance {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.375rem 1.25rem;
    margin: 0.75rem 0 0;
    padding: 0;
    list-style: none;
    font-size: 0.8125rem;
    color: $dark-font-muted;

    li {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
    }

    svg,
    :deep(b) {
        color: $dark-font;
    }
}

// Ordinateur (formulaire sur 2 colonnes) : 4 rangées au lieu de 5. Profession à côté du revenu,
// les deux questions oui/non côte à côte à côté de l'ancienneté.
@media (min-width: 1161px) {
    .simulation-grid--compact {
        grid-template-columns: repeat(4, 1fr);

        & > * {
            grid-column: span 2;
        }

        // Oui / non alignés en bas de la rangée, même si la question tient sur deux lignes
        & > .simulation-grid__choice {
            grid-column: span 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        & > .desktop-no-grid {
            grid-column: 1 / -1;
        }
    }
}

// Écrans d'ordinateur peu hauts (MacBook 14" sous Chrome ≈ 800 px utiles) : tout tient sans défiler,
// textes et champs resserrés en proportion.
@media (min-width: 1024px) and (max-height: 900px) {
    .simulation-grid {
        gap: 1rem;
    }

    .row-margin {
        margin-bottom: 0.5rem;
    }

    .reassurance {
        margin-top: 0.5rem;
    }

    form :deep(.input__label),
    form :deep(.radio-group__label) {
        font-size: 0.9375rem;
        padding-bottom: 0.375rem;
    }

    form :deep(.input) {
        max-height: 2.625rem;
        padding: 0.625rem 0.75rem;
        font-size: 0.9375rem;
    }

    // L'espacement vient de la grille ; une erreur vide ne réserve pas de place
    form :deep(.input__field) {
        margin-bottom: 0;
    }

    form :deep(.input__error:empty) {
        margin: 0;
    }

    // Revenu et ancienneté à la même hauteur que les autres champs
    form :deep(.currency-field__input),
    form :deep(.counter-container) > * {
        height: 2.625rem;
        max-height: 2.625rem;
        box-sizing: border-box;
        font-size: 0.9375rem;
    }

    hr {
        margin: 0.5rem 0;
    }

    // « Vous avez déjà un compte ? Connectez-vous » sur une seule ligne
    .has-account {
        display: flex;
        justify-content: center;
        align-items: baseline;
        gap: 0.375rem;
        margin-top: 0.5rem;
        font-size: 0.9375rem;

        &__message {
            margin: 0;
        }
    }
}
.desktop-no-grid {
    @include medium-up {
        grid-column: 1/3;
    }
}
.input__label {
    padding-bottom: 0.5rem;
    display: block;
}
.row-margin {
    margin-bottom: 1rem;
}

.has-account {
    margin-top: 1.5rem;
    text-align: center;

    &__message {
        padding: 0;
        margin: 0 0 0.25rem 0;
    }

    &__button {
        background: none;
        border: none;
        text-decoration: underline;
        font-weight: 700;

        &:hover {
            cursor: pointer;
            text-decoration: none;
        }
    }
}

.cta {
    margin: 1.5rem 0;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;

    @include medium-down {
        grid-template-columns: repeat(1, 1fr);

        & > *:first-child {
            order: 2;
        }
    }

    & > * {
        width: 100%;
    }
}

.compute-button {
    width: 100%;
    margin-top: 1rem;
}

.compute-progress {
    font-family: $bebas;
    color: $primary;
    font-size: 1.75rem;
    text-transform: uppercase;
    padding: 2rem 0;
    text-align: center;
    display: block;
    width: 100%;

    @include medium-down {
        font-size: 1.25rem;
    }
}
</style>

<style lang="scss">
// Étape 1 sur un ordinateur peu haut : marge du haut et message d'Alexis plus compacts
// (éléments de la mise en page commune, hors de portée des styles scoped de la page).
@media (min-width: 1024px) and (max-height: 900px) {
    .simulator:has(.simulation-grid--compact) {
        .simulator__content {
            padding-top: 1.5rem;
            padding-bottom: 1rem;
        }

        .simulator__alexis {
            margin-bottom: 0.5rem;

            img {
                width: 40px;
                height: 40px;
            }
        }

        .simulator__alexis__name,
        .simulator__alexis__job {
            font-size: 0.9375rem;
            line-height: 1.25;
        }

        .simulator__message {
            padding: 0.75rem 1.25rem;
            margin-bottom: 1rem;
            font-size: 0.9375rem;
        }
    }
}
</style>
